/**
 * "UPB over time" — the purchased loan's balance from origination to maturity,
 * with an optional curtailment what-if. Rendered on the Summary tab and on
 * Trade economics (one component, so the two never disagree).
 *
 *   — Current UPB      solid + area: origination → pre-purchase draws → cut-off,
 *                      then the scheduled balance to maturity (upbModel.js)
 *   - - Original UPB   reference line at the amount at the correspondent close
 *   - - With curtailment  dashed: the same schedule after a one-time principal
 *                      reduction in the chosen month. A what-if only — nothing
 *                      is saved.
 *
 * Colors validated with the dataviz palette check: brand teal-500 vs amber
 * dark pass CVD + normal-vision separation; teal-500 is under 3:1 on white,
 * so every value it carries is also printed in the footer (the relief).
 */
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AgCharts } from "ag-charts-react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import DollarInput from "../../../../components/inputs/DollarInput";
import { TAB_TOKENS as T } from "../tabLayout/tabTokens";
import { amber, slate, teal } from "../../../../theme/colors";
import {
  buildUpbSeries,
  compactDollar,
  curtailmentMonths,
  money,
  monthLabel,
  shortDate,
  upbFacts,
} from "./upbModel";

const UPB = teal[500];
const CURTAIL = amber.dark;
const GUIDE = slate[500];

const LegendKey = ({ color, dashed, label }) => (
  <Flx ac sx={{ gap: "7px" }}>
    <Box
      sx={{
        width: 18,
        borderTop: `2px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
    <Txt sx={{ fontSize: 12.5, color: T.color.ink2 }}>{label}</Txt>
  </Flx>
);

LegendKey.propTypes = {
  color: PropTypes.string,
  dashed: PropTypes.bool,
  label: PropTypes.node,
};

const FooterStat = ({ label, value, tone }) => (
  <Txt sx={{ fontSize: 12.5, color: tone || T.color.ink3 }}>
    {label}{" "}
    <Box
      component="span"
      sx={{
        fontWeight: 650,
        color: tone || T.color.ink,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value ?? "—"}
    </Box>
  </Txt>
);

FooterStat.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  tone: PropTypes.string,
};

const controlSx = {
  "& .MuiOutlinedInput-root": {
    height: 34,
    fontSize: 13,
    borderRadius: "6px",
    background: "#fff",
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: slate[200] },
};

const UpbOverTimeCard = ({ loan, sx }) => {
  const facts = useMemo(() => upbFacts(loan), [loan]);
  const months = useMemo(
    () => curtailmentMonths(facts.cutOff, facts.maturity),
    [facts.cutOff, facts.maturity],
  );
  const [amount, setAmount] = useState(null);
  const [month, setMonth] = useState("");
  const monthOpt = months.find((m) => m.value === month) || months[0];

  const curtailDate = amount > 0 ? monthOpt?.date : null;
  const s = useMemo(
    () =>
      buildUpbSeries(
        loan,
        curtailDate ? { amount, date: curtailDate } : null,
      ),
    [loan, amount, curtailDate],
  );

  const hasChart = s.upb.length >= 2;
  const showOriginal = s.originalUPB != null && s.originalUPB !== s.currentUPB;
  const showCeiling =
    s.fullyDrawn != null &&
    s.currentUPB != null &&
    s.fullyDrawn > s.currentUPB &&
    s.fullyDrawn > (s.originalUPB ?? 0);

  const options = useMemo(() => {
    if (!hasChart) return null;
    const tooltip = (fallback) => ({
      renderer: ({ datum }) => ({
        heading: shortDate(datum.date),
        title: datum.note || fallback(datum),
        data: [{ label: "UPB", value: money(datum.value, 2) }],
      }),
    });
    const yCross = [];
    if (showOriginal)
      yCross.push({
        type: "line",
        value: s.originalUPB,
        stroke: GUIDE,
        strokeWidth: 1.5,
        lineDash: [5, 4],
      });
    if (showCeiling)
      yCross.push({
        type: "line",
        value: s.fullyDrawn,
        stroke: slate[300],
        strokeWidth: 1,
        lineDash: [2, 4],
        label: {
          text: `Fully drawn ${compactDollar(s.fullyDrawn)}`,
          position: "top-right",
          color: slate[500],
          fontSize: 11,
        },
      });
    const series = [
      {
        type: "area",
        data: s.upb,
        xKey: "date",
        yKey: "value",
        yName: "Current UPB",
        stroke: UPB,
        strokeWidth: 2,
        fill: UPB,
        fillOpacity: 0.1,
        marker: { enabled: false },
        tooltip: tooltip((d) =>
          d.date > s.cutOff ? "Scheduled balance" : "Balance",
        ),
      },
    ];
    if (s.curtailed?.points?.length)
      series.push({
        type: "line",
        data: s.curtailed.points,
        xKey: "date",
        yKey: "value",
        yName: "With curtailment",
        stroke: CURTAIL,
        strokeWidth: 2,
        lineDash: [6, 4],
        marker: { enabled: false },
        tooltip: tooltip(() => "With curtailment"),
      });
    return {
      height: 280,
      background: { visible: false },
      padding: { top: 16, right: 12, bottom: 4, left: 4 },
      legend: { enabled: false },
      series,
      axes: [
        {
          type: "time",
          position: "bottom",
          nice: false,
          line: { enabled: false },
          gridLine: { enabled: false },
          label: { color: slate[500], fontSize: 11, format: "%b '%y" },
          crossLines: [
            {
              type: "line",
              value: s.cutOff,
              stroke: slate[400],
              strokeWidth: 1,
              lineDash: [2, 4],
              label: {
                text: `Cut-off ${monthLabel(s.cutOff).split(" ")[0]} ${s.cutOff.getDate()}`,
                position: "top-right",
                color: slate[500],
                fontSize: 11,
              },
            },
          ],
        },
        {
          type: "number",
          position: "left",
          min: 0,
          line: { enabled: false },
          gridLine: { style: [{ stroke: slate[200], lineDash: [] }] },
          label: {
            color: slate[500],
            fontSize: 11,
            formatter: ({ value }) => compactDollar(value),
          },
          crossLines: yCross,
        },
      ],
    };
  }, [hasChart, s, showCeiling, showOriginal]);

  // Subtitle names the shape of the loan the way the note does.
  const shape = s.amortizing
    ? (s.amortizationType || "amortizing").toLowerCase()
    : "interest only";
  const subtitle = [
    s.origination ? "Origination to maturity" : "Cut-off to maturity",
    s.maturity
      ? s.amortizing
        ? `${shape}, matures ${shortDate(s.maturity)}`
        : `${shape}, balloon due ${shortDate(s.maturity)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const canSimulate = !!s.schedule && months.length > 0;

  return (
    <Box
      sx={{
        background: T.color.surface,
        border: `1px solid ${T.color.border}`,
        borderRadius: T.radius.card,
        boxShadow: T.shadow.card,
        p: "20px 22px 18px",
        ...sx,
      }}
    >
      <Flx jb sx={{ gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
        <Flx column sx={{ gap: "3px", minWidth: 0 }}>
          <Txt
            component="h2"
            sx={{ fontSize: 15, fontWeight: 650, color: T.color.ink, m: 0 }}
          >
            UPB over time
          </Txt>
          <Txt sx={{ fontSize: 12.5, color: T.color.ink3 }}>{subtitle}</Txt>
        </Flx>
        {canSimulate && (
          <Flx ac sx={{ gap: "8px", flexWrap: "wrap" }}>
            <Txt sx={{ fontSize: 12.5, color: T.color.ink2 }}>
              Simulate curtailment
            </Txt>
            <Box sx={{ width: 150, ...controlSx }}>
              <DollarInput
                value={amount ?? ""}
                onChange={(v) => {
                  if (typeof v === "number" || v == null) setAmount(v);
                }}
                size="small"
                placeholder="0"
                fullWidth
              />
            </Box>
            <Txt sx={{ fontSize: 12.5, color: T.color.ink3 }}>in</Txt>
            <Select
              size="small"
              value={monthOpt?.value || ""}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ ...controlSx["& .MuiOutlinedInput-root"], minWidth: 118 }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </Flx>
        )}
      </Flx>

      {hasChart ? (
        <>
          <Flx ac sx={{ gap: "18px", mt: "14px", flexWrap: "wrap" }}>
            <LegendKey color={UPB} label="Current UPB" />
            {showOriginal && (
              <LegendKey color={GUIDE} dashed label="Original UPB" />
            )}
            {s.curtailed && (
              <LegendKey color={CURTAIL} dashed label="With curtailment" />
            )}
          </Flx>
          <Box sx={{ mt: "6px" }}>
            <AgCharts options={options} />
          </Box>
          <Flx
            ac
            sx={{
              gap: "8px 28px",
              flexWrap: "wrap",
              pt: "14px",
              mt: "6px",
              borderTop: `1px solid ${T.color.border}`,
            }}
          >
            <FooterStat label="UPB at cut-off" value={money(s.currentUPB)} />
            {s.schedule &&
              (s.amortizing ? (
                <FooterStat
                  label="Scheduled payoff"
                  value={monthLabel(s.schedule.payoff)}
                />
              ) : (
                <FooterStat
                  label="Balloon at maturity"
                  value={money(s.schedule.balloon)}
                />
              ))}
            {s.curtailed &&
              (s.amortizing ? (
                <FooterStat
                  label="Payoff with curtailment"
                  value={monthLabel(s.curtailed.payoff)}
                />
              ) : (
                <FooterStat
                  label="Balloon with curtailment"
                  value={money(s.curtailed.balloon)}
                />
              ))}
            {s.curtailed?.interestReduced != null && (
              <FooterStat
                label="Interest income reduced ≈"
                value={money(s.curtailed.interestReduced)}
                tone={amber.chipText}
              />
            )}
          </Flx>
        </>
      ) : (
        <Txt
          component="div"
          sx={{
            fontSize: 12.5,
            color: T.color.ink3,
            py: "28px",
            textAlign: "center",
          }}
        >
          Add the current UPB and a cut-off or settlement date on Trade
          economics to chart this loan&apos;s balance.
        </Txt>
      )}
    </Box>
  );
};

UpbOverTimeCard.propTypes = {
  loan: PropTypes.object,
  sx: PropTypes.object,
};

export default UpbOverTimeCard;
